import { useState } from 'react';
import { toast } from 'sonner';
import type { FeedbackCategory, FeedbackRequest } from '@timato/shared';
import { ApiError, api } from '@/auth/api';
import { labelFromNavigator } from '@/auth/deviceLabel';
import { useAuth } from '@/auth/store';
import { Button, Field, Input, Modal, SegmentedControl, Textarea, type Segment } from '@/components/ui';
import { readThemePref, resolveTheme } from '@/lib/theme';
import { useOnline } from '@/lib/useOnline';
import { useFeedback } from './store';

const CATEGORIES: Segment<FeedbackCategory>[] = [{ value: 'bug', label: 'Bug' }, { value: 'idea', label: 'Idea' }, { value: 'question', label: 'Question' }];
const MAX = 4000;

type Draft = { message: string; category: FeedbackCategory | ''; email: string };
/** What was typed survives closing the dialog without sending, until it is sent: a stray Esc must not cost a bug report. */
let draft: Draft | null = null;

/** Send feedback (TW-8): a message, an optional kind, and where to reply. Mounted once in the shell. */
export function FeedbackDialog() {
  const { open, setOpen } = useFeedback();
  return open ? <FeedbackForm onClose={() => setOpen(false)} /> : null;
}

function FeedbackForm({ onClose }: { onClose: () => void }) {
  const signedIn = useAuth((s) => s.status === 'signedIn');
  const account = useAuth((s) => s.user?.email);
  const online = useOnline();
  const [message, setMessage] = useState(draft?.message ?? '');
  const [category, setCategory] = useState<FeedbackCategory | ''>(draft?.category ?? '');
  const [email, setEmail] = useState(draft?.email ?? '');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string>();
  // Shown before sending, so nothing leaves the device that the person hasn't seen.
  const [context] = useState(() => ({ version: __APP_VERSION__, platform: labelFromNavigator(), theme: resolveTheme(readThemePref()), signedIn }));
  const replyTo = signedIn ? account : email.trim() || undefined;

  const dismiss = () => { draft = message.trim() || email.trim() || category ? { message, category, email } : null; onClose(); };
  const send = async () => {
    setSending(true); setError(undefined);
    try {
      const body: FeedbackRequest = { message, category: category || undefined, email: signedIn ? undefined : email.trim() || undefined, context };
      await api('/v1/feedback', { json: body });
      draft = null; onClose();
      toast('Thanks, feedback sent', { description: replyTo ? `We'll reply to ${replyTo}.` : "There's no address to reply to, but we read every message." });
    } catch (e) {
      setError(e instanceof ApiError && e.status === 429 ? 'Too many messages in a short time. Try again in a minute.'
        : e instanceof ApiError && e.status === 400 ? 'That email address doesn\'t look right.'
        : "Couldn't send it. Your message is still here, so try again in a moment.");
    } finally { setSending(false); }
  };

  return (
    <Modal open onOpenChange={(o) => { if (!o) dismiss(); }} title="Send feedback" description="A bug, an idea or a question. It reaches the people who build Timato."
      footer={<><Button variant="secondary" size="dialog" onClick={dismiss}>Cancel</Button><Button variant="primary" size="dialog" disabled={!message.trim() || sending || !online} onClick={() => void send()}>{sending ? 'Sending…' : 'Send'}</Button></>}>
      <form className="grid gap-5" onSubmit={(e) => { e.preventDefault(); if (message.trim() && online) void send(); }}>
        <Field label="Kind">{() => <SegmentedControl aria-label="Kind of feedback" value={category as FeedbackCategory} onChange={setCategory} options={CATEGORIES} size="sm" />}</Field>
        <Field label="Message" hint={message.length > MAX - 200 ? `${MAX - message.length} characters left` : undefined}>{(id) => (
          <Textarea id={id} autoFocus value={message} onChange={(e) => setMessage(e.target.value)} maxLength={MAX} rows={6}
            placeholder="What happened, or what would make Timato better for you?" />
        )}</Field>
        <Field label="Reply to" hint={signedIn ? 'Your account address.' : "Optional. Leave it empty and we can't write back."}>{(id) => (
          <Input id={id} type="email" value={signedIn ? account ?? '' : email} readOnly={signedIn} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" />
        )}</Field>
        <p className="text-meta-sm text-ink-2 m-0">
          Sent along with it: Timato {context.version} · {context.platform} · {context.theme} theme · {signedIn ? 'signed in' : 'signed out'}. Nothing about your sessions, projects or tasks.
        </p>
        {!online && <p className="text-meta-sm text-ink-2 m-0">You're offline. Your message stays here until you're back.</p>}
        {error && <p role="alert" className="text-meta-sm text-danger-fg m-0">{error}</p>}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
